import { defineStore } from "pinia";
import ApiManager from "~/code/api-manager";
import {
  OnibiPortalCreateEditRoleDto,
  OnibiPortalRoleDto,
  OnibiPortalUserDto,
  Permission,
} from "~/generated/models";
import { EventBus } from "~/code/event-bus";
import { ResetPasswordDto } from "~/generated/models";

export const useUserStore = defineStore("user", {
  state: () => ({
    users: [] as OnibiPortalUserDto[],
    roles: [] as OnibiPortalRoleDto[],
    permissions: [] as Permission[],
    permissionsLoaded: false,
    permissionsLoadPromise: null as Promise<void> | null,
    loading: false,
    errors: [] as any[],
    totalItems: 0,
  }),
  actions: {
    permissionNameById(permissionId: number): string | null {
      const permission = this.permissions.find((entry) => entry.id === permissionId);
      return permission?.name || null;
    },

    async ensurePermissionsLoaded() {
      if (this.permissionsLoaded && this.permissions.length > 0) {
        return;
      }

      if (this.permissionsLoadPromise) {
        await this.permissionsLoadPromise;
        return;
      }

      this.permissionsLoadPromise = this.fetchPermissions(true)
        .catch(() => undefined)
        .finally(() => {
          this.permissionsLoadPromise = null;
        });

      await this.permissionsLoadPromise;
    },

    // addUser method
    async addUser(user: OnibiPortalUserDto) {
      this.loading = true;
      const apiManager = ApiManager.getInstance();

      try {
        const response = await apiManager.apis["UserApi"].apiUserCreatePut(
          user
        );

        if (response.status === 200) {
          this.users.unshift(response.data);
          EventBus.emit("toast", {
            type: "success",
            message: "User added successfully!",
          });
        } else {
          if (response.data?.errors) {
            response.data.errors.forEach((error) => {
              EventBus.emit("toast", {
                type: "error",
                message: `Error adding user ${error.code}: ${error.description}`,
              });
            });
            this.errors = response.data.errors;
          }
        }
      } catch (error) {
        EventBus.emit("toast", {
          type: "error",
          message: "Error adding user: " + error.message,
        });
        this.errors.push(error.message);
      } finally {
        this.loading = false;
      }
    },
    async recreateUser(user: OnibiPortalUserDto) {
      this.loading = true;
      const apiManager = ApiManager.getInstance();

      try {
        const response = await apiManager.apis["UserApi"].reactivateUser(user);

        if (response.status === 200) {
          user.isDeleted = false;
          EventBus.emit("toast", {
            type: "success",
            message: "User reactivated successfully!",
          });
        } else {
          if (response.errors) {
            response.errors.forEach((error) => {
              EventBus.emit("toast", {
                type: "error",
                message: `Error reactivating user ${error.message}`,
              });
            });
            this.errors = response.errors;
          }
        }
      } catch (error) {
        EventBus.emit("toast", {
          type: "error",
          message: "Error reactivating user: " + error.message,
        });
        this.errors.push(error.message);
      } finally {
        this.loading = false;
      }
    },
    // addUser method
    async reactivateUser(user: OnibiPortalUserDto) {
      this.loading = true;
      const apiManager = ApiManager.getInstance();

      try {
        const response = await apiManager.apis["UserApi"].apiUserActivatePost(
          user.id
        );

        if (response.status === 200) {
          user.isDeleted = false;
          EventBus.emit("toast", {
            type: "success",
            message: "User activated successfully!",
          });
        } else {
          if (response.data?.errors) {
            response.data.errors.forEach((error) => {
              EventBus.emit("toast", {
                type: "error",
                message: `Error activate user ${error.code}: ${error.description}`,
              });
            });
            this.errors = response.data.errors;
          }
        }
      } catch (error) {
        EventBus.emit("toast", {
          type: "error",
          message: "Error activate user: " + error.message,
        });
        this.errors.push(error.message);
      } finally {
        this.loading = false;
      }
    },

    // deleteUser method
    async deleteUser(user: OnibiPortalUserDto) {
      this.loading = true;
      const apiManager = ApiManager.getInstance();

      try {
        const response = await apiManager.apis["UserApi"].apiUserDeleteDelete(
          user.id
        );

        if (response.status === 200) {
          const index = this.users.findIndex((u) => u.id === user.id);
          this.users[index].isDeleted = true;

          EventBus.emit("toast", {
            type: "success",
            message: "User deleted successfully!",
          });
        } else {
          if (response.data?.errors) {
            response.data.errors.forEach((error) => {
              EventBus.emit("toast", {
                type: "error",
                message: `Error deleting user: ${error.code} - ${error.description}`,
              });
            });
            this.errors = response.data.errors;
          }
        }
      } catch (error) {
        EventBus.emit("toast", {
          type: "error",
          message: `Error deleting user: ${error.message}`,
        });
        this.errors.push(error.message);
      } finally {
        this.loading = false;
      }
    },

    // updateUser method
    async updateUser(user: OnibiPortalUserDto) {
      this.loading = true;
      const apiManager = ApiManager.getInstance();

      try {
        const response = await apiManager.apis["UserApi"].apiUserEditPost(user);

        if (response.status === 200) {
          const index = this.users.findIndex((u) => u.id === user.id);
          if (index >= 0) {
            this.users[index] = user;
          }
          EventBus.emit("toast", {
            type: "success",
            message: "User updated successfully!",
          });
        } else {
          if (response.data?.errors) {
            response.data.errors.forEach((error) => {
              EventBus.emit("toast", {
                type: "error",
                message: `Error updating user: ${error.code} - ${error.description}`,
              });
            });
            this.errors = response.data.errors;
          }
        }
      } catch (error) {
        EventBus.emit("toast", {
          type: "error",
          message: `Error updating user: ${error.message}`,
        });
        this.errors.push(error.message);
      } finally {
        this.loading = false;
      }
    },

    // fetchUsers method
    async fetchUsers(
      page = 1,
      pageSize = 10,
      sortField = "DateCreated",
      sortOrder = "asc",
      searchTerm: string | null = null,
      includeInactive = false
    ) {
      this.loading = true;
      const apiManager = ApiManager.getInstance();
      const includeDeleted = includeInactive;
      const includeActive = !includeInactive;

      try {
        const response = await apiManager.apis["UserApi"].apiUserGetAllGet(
          page,
          pageSize,
          sortField,
          sortOrder === "desc",
          includeDeleted,
          includeActive,
          searchTerm ?? undefined
        );
        if (response.status === 200 && response.data) {
          this.users = response.data.items || [];
          this.totalItems = response.data.totalItems || 0;
        } else {
          if (response.data?.errors) {
            response.data.errors.forEach((error) => {
              EventBus.emit("toast", {
                type: "error",
                message: `Error fetching users: ${error.code} - ${error.description}`,
              });
            });
            this.errors = response.data.errors;
          }
        }
      } catch (error) {
        EventBus.emit("toast", {
          type: "error",
          message: `Error fetching users: ${error.message}`,
        });
        this.errors.push(error.message);
      } finally {
        this.loading = false;
      }
    },

    // addRole method
    async addRole(name: string, permissions: number[]) {
      this.loading = true;
      const apiManager = ApiManager.getInstance();

      const payload: OnibiPortalRoleDto = {
        Name: name,
        RolePermissionIds: permissions,
      };

      try {
        const response = await apiManager.apis["UserApi"].apiUserCreateRolePut(
          payload
        );
        if (response.status === 200) {
          const createdRole = response.data;
          this.roles.unshift(createdRole);
          EventBus.emit("toast", {
            type: "success",
            message: "Role added!",
          });
        } else {
          if (response.data?.errors) {
            response.data.errors.forEach((error) => {
              EventBus.emit("toast", {
                type: "error",
                message: `Role add failed: ${error.code} - ${error.description}`,
              });
            });
            this.errors = response.data.errors;
          }
        }
      } catch (error) {
        EventBus.emit("toast", {
          type: "error",
          message: `Nope, couldn't add role: ${error.message}`,
        });
        this.errors.push(error.message);
      } finally {
        this.loading = false;
      }
    },
    // deleteRole method
    async deleteRole(role: { id: string; name: string }) {
      this.loading = true;
      const apiManager = ApiManager.getInstance();

      try {
        const response = await apiManager.apis[
          "UserApi"
        ].apiUserDeleteRoleDelete(role.id);
        if (response.status === 200) {
          const index = this.roles.findIndex((r) => r.id === role.id);
          if (index >= 0) {
            this.roles.splice(index, 1);
          }
          EventBus.emit("toast", {
            type: "success",
            message: "Role deleted successfully!",
          });
        } else {
          if (response.data?.errors) {
            response.data.errors.forEach((error) => {
              EventBus.emit("toast", {
                type: "error",
                message: `Error deleting role: ${error.code} - ${error.description}`,
              });
            });
            this.errors = response.data.errors;
          }
        }
      } catch (error) {
        EventBus.emit("toast", {
          type: "error",
          message: `Error deleting role: ${error.message}`,
        });
        this.errors.push(error.message);
      } finally {
        this.loading = false;
      }
    },

    // updateRole method
    async updateRole(roleId: string, roleName: string, newPermissions: any[]) {
      this.loading = true;
      const apiManager = ApiManager.getInstance();

      const payload: OnibiPortalCreateEditRoleDto = {
        id: roleId,
        name: roleName,
        rolePermissionIds: newPermissions,
      };
      try {
        const response = await apiManager.apis["UserApi"].apiUserEditRolePost(
          payload
        );
        if (response.status === 200) {
          const roleIndex = this.roles.findIndex((r) => r.id === roleId);
          if (roleIndex >= 0) {
            const mappedPermissions = newPermissions.map((id) => {
              const permission = this.permissions.find((p) => p.id === id);
              return {
                permissionId: id,
                permissionName: permission ? permission.name : "",
              };
            });

            this.roles[roleIndex].rolePermissions = mappedPermissions;
            this.roles[roleIndex].name = roleName;
          }
          EventBus.emit("toast", {
            type: "success",
            message: "Permissions updated!",
          });
        } else {
          if (response.data?.errors) {
            response.data.errors.forEach((error) => {
              EventBus.emit("toast", {
                type: "error",
                message: `Error updating permissions: ${error.code} - ${error.description}`,
              });
            });
            this.errors = response.data.errors;
          }
        }
      } catch (error) {
        EventBus.emit("toast", {
          type: "error",
          message: `Couldn't update permissions: ${error.message}`,
        });
        this.errors.push(error.message);
      } finally {
        this.loading = false;
      }
    },
    // fetchRoles method
    async fetchRoles(
      page = 1,
      pageSize = 10,
      sortField = "id",
      sortOrder = "asc",
      searchTerm: string | null = null
    ) {
      this.loading = true;
      const apiManager = ApiManager.getInstance();

      try {
        const response = await apiManager.apis["UserApi"].apiUserGetAllRolesGet(
          page,
          pageSize,
          sortField,
          sortOrder === "desc",
          searchTerm
        );
        if (response.status === 200 && response.data) {
          this.roles = response.data || [];
        } else {
          if (response.data?.errors) {
            response.data.errors.forEach((error) => {
              EventBus.emit("toast", {
                type: "error",
                message: `Error fetching roles: ${error.code} - ${error.description}`,
              });
            });
            this.errors = response.data.errors;
          }
        }
      } catch (error) {
        EventBus.emit("toast", {
          type: "error",
          message: `Error fetching roles: ${error.message}`,
        });
        this.errors.push(error.message);
      } finally {
        this.loading = false;
      }
    },
    async fetchPermissions(silent = false) {
      if (!silent) {
        this.loading = true;
      }
      const apiManager = ApiManager.getInstance();

      try {
        const { data } = await apiManager.apis[
          "UserApi"
        ].apiUserGetAllPermissionsGet();

        this.permissions = data;
        this.permissionsLoaded = true;
      } catch (error) {
        this.permissionsLoaded = false;
        EventBus.emit("toast", {
          type: "error",
          message: `Couldn't fetch permissions: ${error.message}`,
        });
      } finally {
        if (!silent) {
          this.loading = false;
        }
      }
    },
    async createPermission(permissionName: string) {
      this.loading = true;
      const apiManager = ApiManager.getInstance();

      try {
        const response = await apiManager.apis[
          "UserApi"
        ].apiUserCreatePermissionPut(permissionName);
        if (response.status === 200) {
          this.permissions.push(response.data);
          EventBus.emit("toast", {
            type: "success",
            message: "Permission created!",
          });
        }
      } catch (error) {
        EventBus.emit("toast", {
          type: "error",
          message: `Failed to create permission: ${error.message}`,
        });
      } finally {
        this.loading = false;
      }
    },
    async deletePermission(permissionId: number) {
      this.loading = true;
      const apiManager = ApiManager.getInstance();

      try {
        await apiManager.apis["UserApi"].apiUserDeletePermissionDelete(
          permissionId
        );
        const index = this.permissions.findIndex((p) => p.id === permissionId);
        if (index >= 0) this.permissions.splice(index, 1);
        EventBus.emit("toast", {
          type: "success",
          message: "Permission deleted!",
        });
      } catch (error) {
        EventBus.emit("toast", {
          type: "error",
          message: `Failed to delete permission: ${error.message}`,
        });
      } finally {
        this.loading = false;
      }
    },
    async editPermission(permissionId: string, newPermissionName: string) {
      this.loading = true;
      const apiManager = ApiManager.getInstance();

      try {
        await apiManager.apis["UserApi"].apiUserEditPermissionPost(
          permissionId,
          newPermissionName
        );
        const permission = this.permissions.find((p) => p.id === permissionId);
        if (permission) permission.name = newPermissionName;
        EventBus.emit("toast", {
          type: "success",
          message: "Permission updated!",
        });
      } catch (error) {
        EventBus.emit("toast", {
          type: "error",
          message: `Failed to update permission: ${error.message}`,
        });
      } finally {
        this.loading = false;
      }
    },
  },
});
